import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

export default function PackagesManager() {
  const [courses, setCourses] = useState([]);
  const [tiers, setTiers] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/packages`, { withCredentials: true });
      setCourses(data.courses || []);
      setTiers(data.tiers || {});
    } catch {
      toast.error("Could not load packages");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (tid, slug) => {
    setTiers((prev) => {
      const current = prev[tid]?.courses || [];
      const next = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug];
      return { ...prev, [tid]: { ...prev[tid], courses: next } };
    });
  };

  const save = async (tid) => {
    setSaving(tid);
    try {
      await axios.put(
        `${API}/admin/pricing`,
        { tiers: { [tid]: { courses: tiers[tid]?.courses || [] } } },
        { withCredentials: true },
      );
      toast.success(`${tiers[tid]?.name || tid} updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  const ordered = Object.values(tiers).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="admin-packages-manager" className="space-y-5">
      <p className="text-sm text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
        Tick the courses that are always included in each package. Leave a package empty to let the
        buyer choose any courses up to the package size at checkout. Changes apply immediately to new
        enrollments.
      </p>

      <div className="grid lg:grid-cols-3 gap-5">
        {ordered.map((t) => {
          const picked = t.courses || [];
          return (
            <div
              key={t.id}
              data-testid={`package-card-${t.id}`}
              className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-[#06B6D4]" />
                  <h3 className="font-display text-base font-semibold tracking-tight">{t.name}</h3>
                </div>
                <span className="text-xs font-semibold text-[#2563EB]">{rupees(t.price_paise)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Package size: {t.course_count} course(s) · {picked.length} pinned
              </p>

              <div className="mt-3 space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
                {courses.map((c) => (
                  <label
                    key={c.slug}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      data-testid={`package-${t.id}-course-${c.slug}`}
                      checked={picked.includes(c.slug)}
                      onChange={() => toggle(t.id, c.slug)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="truncate">{c.title}</span>
                  </label>
                ))}
              </div>

              <button
                type="button"
                onClick={() => save(t.id)}
                disabled={saving === t.id}
                data-testid={`package-save-${t.id}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" /> {saving === t.id ? "Saving…" : "Save package"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
